import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/DashboardMenuHeader';
import { ChevronDown, Search, ChevronRight, RefreshCw, Filter, FilterX, X, TrendingDown, TrendingUp, Tag, Users, Download, Calendar, Package, DollarSign } from 'lucide-react';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */

const filterLabels = {
  BI_PROVEEDOR: "Casas",
  BI_MARCA: "Marca",
  BI_LINEA: "Línea",
  CLI_SEG_DES: "Segmento",
  CLI_PRIN: "Cliente",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_REG_VERD: "Región Filtro",
  V_ANULADA: "Factura Anulada",
  CLI_INACTIVO: "Cliente Inactivo",
  BI_CLASIF2: "Clasificación",
  BI_DESC1: "Producto",
  CLI_TERRITORIO: "Territorio",
  CLI_CANAL_REAL: "Canal 2",
  BI_SEGMENTO: "Portafolio",
  CLI_NOMBRE: "Cliente Detalle",
  V_FACTURA: "Factura",
  CLIENTE_ID: "Cliente ID",
  ART_ID: "SKU ID",
  BI_ASP: "ASP",
  ESPECIALISTA: "Especialista",   // 🔧 NUEVO
  USER_ESP: "Usuario Esp.",       // 🔧 NUEVO
};

const formatNumber = (num, isUSD = false) => {
  if (num == null) return isUSD ? '$0' : '0';
  const value = Number(num);
  const formatted = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  return isUSD ? `$${formatted}` : formatted;
};

const formatDollar = (num) => formatNumber(num, true);

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000';
  if (val < 90) return '#ff4d4d';
  if (val < 100) return '#ffcc00';
  return '#00ba52';
};

const getOptValue = (opt) => {
  if (opt == null) return '';
  if (typeof opt === 'object' && 'value' in opt) return String(opt.value ?? '');
  return String(opt);
};
const getOptLabel = (opt) => {
  if (opt == null) return '';
  if (typeof opt === 'object' && 'label' in opt) return opt.label ?? '';
  return String(opt);
};

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  const normalizedSelected = useMemo(
    () => (selectedValues || []).map(v => String(v)),
    [selectedValues]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = normalizedSelected.length > 0;
  const allOptionsSelected = totalOptions > 0 && normalizedSelected.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (normalizedSelected.length === 1
          ? (() => {
              const found = options.find(opt => getOptValue(opt) === normalizedSelected[0]);
              return found != null ? getOptLabel(found) : normalizedSelected[0];
            })()
          : `${normalizedSelected.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(getOptLabel(opt)).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);

  const areAllSelected = filteredOptions.length > 0 && filteredOptions.every(opt =>
    normalizedSelected.includes(getOptValue(opt))
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = (selectedValues || []).filter(val =>
        !filteredOptions.some(opt => getOptValue(opt) === String(val))
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => getOptValue(opt));
      onChange(name, newValuesOnly);
    }
  };

  const handleSingleToggle = useCallback((optValue) => {
    const optStr = String(optValue);
    const exists = normalizedSelected.includes(optStr);
    const newVals = exists
      ? (selectedValues || []).filter(v => String(v) !== optStr)
      : [...(selectedValues || []), optStr];
    onChange(name, newVals);
  }, [selectedValues, normalizedSelected, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input
                type="checkbox"
                checked={areAllSelected}
                onChange={handleSelectAll}
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = getOptValue(opt);
              const optLabel = getOptLabel(opt);
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={normalizedSelected.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onChange(name, [])}>
              Limpiar ({normalizedSelected.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const INITIAL_FILTERS = {
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], CLI_SEG_DES: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_REG_VERD: [],
  V_ANULADA: [], CLI_INACTIVO: [], BI_CLASIF2: [], BI_DESC1: [],
  CLI_TERRITORIO: [], CLI_CANAL_REAL: [], BI_SEGMENTO: [], CLI_NOMBRE: [],
  V_FACTURA: [], CLIENTE_ID: [], ART_ID: [], BI_ASP: [],
  ESPECIALISTA: [], USER_ESP: [],   // 🔧 NUEVOS
};

const getBreakdownLabels = (breakdownType) => {
  switch (breakdownType) {
    case 'BI_PROVEEDOR':
      return { title: 'Desempeño por Casa / Marca / SKU', col1: 'Casa / Marca / SKU', col1Export: 'Casa', col2Export: 'Marca', col3Export: '' };
    case 'ASP':
      return { title: 'Desempeño por ASP / Casa / Marca / SKU', col1: 'ASP / Casa / Marca / SKU', col1Export: 'ASP', col2Export: 'Casa', col3Export: 'Marca' };
    case 'REGION':
      return { title: 'Desempeño por Región / Asesor / Cliente / SKU', col1: 'Región / Asesor / Cliente / SKU', col1Export: 'Región', col2Export: 'Asesor', col3Export: 'Cliente' };
    case 'REG_ASP':
      return { title: 'Desempeño por Región / Vendedor / ASP / SKU', col1: 'Región / Vendedor / ASP / SKU', col1Export: 'Región', col2Export: 'Vendedor', col3Export: 'ASP' };
    case 'ESP_REG_VEN_CLI':   // 🔧 NUEVO
      return {
        title: 'Desempeño por Especialista / Región / Vendedor / Cliente / SKU',
        col1: 'Esp. / Región / Vendedor / Cliente / SKU',
        col1Export: 'Especialista',
        col2Export: 'Región',
        col3Export: 'Vendedor',
        col4Export: 'Cliente Principal',
      };
    case 'CLIENTE':
    case 'CLI_PRIN':
      return { title: 'Desempeño por Cliente / ID / SKU', col1: 'Cliente / ID / SKU', col1Export: 'Cliente', col2Export: 'ID', col3Export: '' };
    default:
      return { title: 'Desempeño por Región / Vendedor / ASP / SKU', col1: 'Región / Vendedor / ASP / SKU', col1Export: 'Región', col2Export: 'Vendedor', col3Export: 'ASP' };
  }
};

const DailyPrincipal = () => {
  const { salesQueryPML, getSalesFilters, loading: apiLoading } = useQuery();
  const { profile } = useAuth();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [proveedorMarcaLineaTotals, setProveedorMarcaLineaTotals] = useState({ cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0 });
  const [metricType, setMetricType] = useState('BULTOS');
  const [breakdownType, setBreakdownType] = useState('REG_ASP');
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedDay, setSelectedDay] = useState([]);
  const currentYear = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [salesConfig, setSalesConfig] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('CUOTA');
  const [devMode, setDevMode] = useState('NONE');
  const [expandedMarcas, setExpandedMarcas] = useState({});
  const [proveedorMarcaLineaTableData, setProveedorMarcaLineaTableData] = useState([]);
  const [expandedProveedores, setExpandedProveedores] = useState({});
  const [expandedClientes, setExpandedClientes] = useState({});
  const [expandedASPs, setExpandedASPs] = useState({});
  const [expandedNivel4, setExpandedNivel4] = useState({});   // 🔧 NUEVO
  const requestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });
  const [lowCompliance, setLowCompliance] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);

  const toggleMarca = (marca) => setExpandedMarcas(prev => ({ ...prev, [marca]: !prev[marca] }));
  const toggleProveedor = (proveedor) => setExpandedProveedores(prev => ({ ...prev, [proveedor]: !prev[proveedor] }));
  const toggleCliente = (cliente) => setExpandedClientes(prev => ({ ...prev, [cliente]: !prev[cliente] }));
  const toggleASP = (asp) => setExpandedASPs(prev => ({ ...prev, [asp]: !prev[asp] }));
  const toggleNivel4 = (name) => setExpandedNivel4(prev => ({ ...prev, [name]: !prev[name] }));   // 🔧 NUEVO

  // 🔧 Limpiar expansibles al cambiar de vista
  useEffect(() => {
    setExpandedProveedores({});
    setExpandedMarcas({});
    setExpandedClientes({});
    setExpandedNivel4({});
  }, [breakdownType]);

  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLocalLoading(true);
      try {
        const config = await getSalesFilters();
        if (!config) throw new Error("Configuración de ventas no disponible.");

        setSalesConfig(config);
        setAvailableOptions(config.availableOptions || {});
        setAvailableYears(config.availableYears || [currentYear]);

        const allSegments = config.availableOptions?.CLI_SEG_DES || [];
        const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA' ];
        const prefilteredSegments = allSegments.filter(segment =>
          !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
        );

        setFilters({
          ...INITIAL_FILTERS,
          V_ANULADA: ['0'],
          CLI_INACTIVO: ['0'],
          CLI_SEG_DES: prefilteredSegments
        });

        const defaultYear = config.currentYear || currentYear;
        setSelectedAnios([defaultYear]);
        setSelectedMeses([nombresMeses[new Date().getMonth()]]);
        filtersInitialized.current = true;
      } catch (err) {
        console.error('Error al cargar la configuración:', err);
      } finally {
        setLocalLoading(false);
      }
    };
    loadDashboardData();
  }, []);

  const handleRefresh = async () => {
    clearCache();
    await fetchData();
  };

  const handleResetFilters = useCallback(() => {
    if (!salesConfig?.availableOptions?.CLI_SEG_DES) return;
    const allSegments = salesConfig.availableOptions.CLI_SEG_DES;
    const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
    const prefilteredSegments = allSegments.filter(segment =>
      !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
    );
    setFilters({
      ...INITIAL_FILTERS,
      V_ANULADA: ['0'],
      CLI_INACTIVO: ['0'],
      CLI_SEG_DES: prefilteredSegments
    });
  }, [salesConfig]);

  const processResponse = useCallback((response) => {
    const rows = response.proveedor_marca_linea_table || [];
    const hasThirdLevel  = rows.some(row => row.col3_name && row.col3_name.trim() !== '');
    const hasFourthLevel = rows.some(row => row.col4_name && row.col4_name.trim() !== '');   // 🔧 NUEVO

    const calcVariacion = (actual, prev) => {
      if (prev == null || isNaN(prev)) return null;
      if (prev === 0) return actual > 0 ? 100 : 0;
      return ((actual - prev) / prev) * 100;
    };

    const tree = {};
    for (let i = 0, len = rows.length; i < len; i++) {
      const row = rows[i];
      const l1 = row.col1_name || 'SIN_ENTIDAD';
      const l2 = row.col2_name || 'SIN_ENTIDAD2';
      const l3 = hasThirdLevel  ? (row.col3_name || 'SIN_CLIENTE') : null;
      const l4 = hasFourthLevel ? (row.col4_name || 'SIN_CLIENTE_PRIN') : null;   // 🔧 NUEVO
      const linea = row.BI_LINEA || 'SIN LÍNEA';
      const prevB = Number(row.real_bultos_prev ?? 0);
      const prevU = Number(row.real_usd_prev ?? 0);

      if (!tree[l1]) tree[l1] = {
        name: l1, cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
        real_bultos_prev: 0, real_usd_prev: 0, marcas: {}
      };
      const n1 = tree[l1];
      n1.cuota_bultos += Number(row.cuota_bultos || 0);
      n1.real_bultos  += Number(row.real_bultos || 0);
      n1.cuota_usd    += Number(row.cuota_usd || 0);
      n1.real_usd     += Number(row.real_usd || 0);
      n1.real_bultos_prev += prevB;
      n1.real_usd_prev    += prevU;

      if (!n1.marcas[l2]) n1.marcas[l2] = {
        name: l2, cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
        real_bultos_prev: 0, real_usd_prev: 0,
        ...(hasThirdLevel ? { clientes: {} } : { lineas: [] })
      };
      const n2 = n1.marcas[l2];
      n2.cuota_bultos += Number(row.cuota_bultos || 0);
      n2.real_bultos  += Number(row.real_bultos || 0);
      n2.cuota_usd    += Number(row.cuota_usd || 0);
      n2.real_usd     += Number(row.real_usd || 0);
      n2.real_bultos_prev += prevB;
      n2.real_usd_prev    += prevU;

      if (hasThirdLevel) {
        if (!n2.clientes[l3]) n2.clientes[l3] = {
          name: l3, cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
          real_bultos_prev: 0, real_usd_prev: 0,
          ...(hasFourthLevel ? { clientes4: {} } : { lineas: [] })   // 🔧 4° nivel
        };
        const n3 = n2.clientes[l3];
        n3.cuota_bultos += Number(row.cuota_bultos || 0);
        n3.real_bultos  += Number(row.real_bultos || 0);
        n3.cuota_usd    += Number(row.cuota_usd || 0);
        n3.real_usd     += Number(row.real_usd || 0);
        n3.real_bultos_prev += prevB;
        n3.real_usd_prev    += prevU;

        if (hasFourthLevel) {
          if (!n3.clientes4[l4]) n3.clientes4[l4] = {
            name: l4, cuota_bultos: 0, real_bultos: 0, cuota_usd: 0, real_usd: 0,
            real_bultos_prev: 0, real_usd_prev: 0, lineas: []
          };
          const n4 = n3.clientes4[l4];
          n4.cuota_bultos += Number(row.cuota_bultos || 0);
          n4.real_bultos  += Number(row.real_bultos || 0);
          n4.cuota_usd    += Number(row.cuota_usd || 0);
          n4.real_usd     += Number(row.real_usd || 0);
          n4.real_bultos_prev += prevB;
          n4.real_usd_prev    += prevU;
          n4.lineas.push({ name: linea, ...row });
        } else {
          n3.lineas.push({ name: linea, ...row });
        }
      } else {
        n2.lineas.push({ name: linea, ...row });
      }
    }

    const processLevel = (arr) => {
      const sorted = arr.sort((a, b) => b.real_bultos - a.real_bultos);
      for (const item of sorted) {
        item.var_bultos = calcVariacion(item.real_bultos, item.real_bultos_prev);
        item.var_usd    = calcVariacion(item.real_usd,    item.real_usd_prev);
        if (item.marcas) {
          item.marcas = Object.values(item.marcas).map(m => {
            m.var_bultos = calcVariacion(m.real_bultos, m.real_bultos_prev);
            m.var_usd    = calcVariacion(m.real_usd,    m.real_usd_prev);
            if (m.clientes) {
              m.clientes = Object.values(m.clientes).map(c => {
                c.var_bultos = calcVariacion(c.real_bultos, c.real_bultos_prev);
                c.var_usd    = calcVariacion(c.real_usd,    c.real_usd_prev);
                if (c.clientes4) {
                  c.clientes4 = Object.values(c.clientes4).map(c4 => {
                    c4.var_bultos = calcVariacion(c4.real_bultos, c4.real_bultos_prev);
                    c4.var_usd    = calcVariacion(c4.real_usd,    c4.real_usd_prev);
                    c4.lineas = c4.lineas.sort((a,b) => b.real_bultos - a.real_bultos).map(l => ({
                      ...l,
                      var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0)),
                      var_usd:    calcVariacion(l.real_usd    || 0, Number(l.real_usd_prev    ?? 0))
                    }));
                    return c4;
                  }).sort((a,b) => b.real_bultos - a.real_bultos);
                } else if (c.lineas) {
                  c.lineas = c.lineas.sort((a,b) => b.real_bultos - a.real_bultos).map(l => ({
                    ...l,
                    var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0)),
                    var_usd:    calcVariacion(l.real_usd    || 0, Number(l.real_usd_prev    ?? 0))
                  }));
                }
                return c;
              }).sort((a,b) => b.real_bultos - a.real_bultos);
            } else if (m.lineas) {
              m.lineas = m.lineas.sort((a,b) => b.real_bultos - a.real_bultos).map(l => ({
                ...l,
                var_bultos: calcVariacion(l.real_bultos || 0, Number(l.real_bultos_prev ?? 0)),
                var_usd:    calcVariacion(l.real_usd    || 0, Number(l.real_usd_prev    ?? 0))
              }));
            }
            return m;
          }).sort((a,b) => b.real_bultos - a.real_bultos);
        }
      }
      return sorted;
    };

    const processedData = processLevel(Object.values(tree));
    const pmlTotalsRaw = response.proveedor_marca_linea_total_table?.[0] || {};

    return {
      proveedorMarcaLineaTableData: processedData,
      proveedorMarcaLineaTotals: {
        cuota_bultos: Number(pmlTotalsRaw.total_cuota_bultos || 0),
        real_bultos: Number(pmlTotalsRaw.total_real_bultos || 0),
        cuota_usd: Number(pmlTotalsRaw.total_cuota_usd || 0),
        real_usd: Number(pmlTotalsRaw.total_real_usd || 0),
        real_bultos_prev: Number(pmlTotalsRaw.total_real_bultos_prev || 0),
        real_usd_prev: Number(pmlTotalsRaw.total_real_usd_prev || 0),
      }
    };
  }, []);

  const exportToExcel = useCallback((data, filename, type) => {
    let rows = [];
    const labels = getBreakdownLabels(breakdownType);

    if (type === 'proveedorMarcaLinea') {
      const pushLinea = (prov, mar, cli, cli4, lin) => {
        const row = {
          [labels.col1Export || 'Nivel1']: prov?.name || '',
          [labels.col2Export || 'Nivel2']: mar?.name  || '',
        };
        if (labels.col3Export) row[labels.col3Export] = cli?.name || '';
        if (labels.col4Export) row[labels.col4Export] = cli4?.name || '';
        row.SKU_ID = lin.ART_ID || '';
        row.SKU    = lin.name;
        row.Cuota_Bultos = Math.round(lin.cuota_bultos || 0);
        row.Real_Bultos  = Math.round(lin.real_bultos  || 0);
        row.Cumplimiento_Bultos = (lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos * 100) : 0).toFixed(1) + '%';
        row.Cuota_USD = Math.round(lin.cuota_usd || 0);
        row.Real_USD  = Math.round(lin.real_usd  || 0);
        row.Cumplimiento_USD = (lin.cuota_usd > 0 ? (lin.real_usd / lin.cuota_usd * 100) : 0).toFixed(1) + '%';
        rows.push(row);
      };

      data.forEach(prov => {
        prov.marcas.forEach(mar => {
          if (mar.clientes) {
            mar.clientes.forEach(cli => {
              if (cli.clientes4) {
                cli.clientes4.forEach(cli4 => {
                  cli4.lineas.forEach(lin => pushLinea(prov, mar, cli, cli4, lin));
                });
              } else {
                cli.lineas.forEach(lin => pushLinea(prov, mar, cli, null, lin));
              }
            });
          } else {
            mar.lineas.forEach(lin => pushLinea(prov, mar, null, null, lin));
          }
        });
      });
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  }, [breakdownType]);

  const fetchData = useCallback(async () => {
    if (!filtersInitialized.current) return;
    const requestId = ++requestRef.current;
    setQuerying(true);
    try {
      const payload = {
        metric_type: metricType,
        comparison_mode: comparisonMode,
        dev_mode: devMode,
        page_anios: selectedAnios.filter(a => a),
        page_meses: selectedMeses.filter(m => m),
        page_dias: selectedDay.filter(d => d),
        filters: filters,
        breakdown_type: breakdownType,
        chart_year: selectedAnios?.[0] || null,
        low_compliance: lowCompliance,
        user_info: profile ? { username: profile.username, cargo: profile.cargo } : null
      };

      const rawResponse = await salesQueryPML(payload);
      if (requestId !== requestRef.current) return;

      const processed = processResponse(rawResponse);
      setProveedorMarcaLineaTableData(processed.proveedorMarcaLineaTableData);
      setProveedorMarcaLineaTotals(processed.proveedorMarcaLineaTotals);
    } catch (err) {
      if (requestId === requestRef.current) console.error("❌ Error fetchData:", err);
    } finally {
      if (requestId === requestRef.current) setQuerying(false);
    }
  }, [comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance, metricType, breakdownType, salesQueryPML, profile, processResponse]);

  const debouncedFetch = useRef();
  useEffect(() => {
    if (!filtersInitialized.current) return;
    debouncedFetch.current = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(debouncedFetch.current);
  }, [comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance, metricType, breakdownType]);

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  const breakdownLabels = useMemo(() => getBreakdownLabels(breakdownType), [breakdownType]);

  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

      {(apiLoading || localLoading) && !filtersInitialized.current ? (
        <FullScreenLoader message="Cargando datos del servidor..." />
      ) : null}
      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Daily Dimassi</h1>
            <div className="title-underline"></div>
          </div>
          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} title="Actualizar Datos" disabled={querying}>
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>
            <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
              <Filter size={16} />
              <span>Filtros</span>
            </button>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`} onClick={() => setComparisonMode('CUOTA')}>Cuota</button>
              <button className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`} onClick={() => setComparisonMode('FORECAST')}>Forecast</button>
            </div>
            <div className="switch-toggle-group">
              <button className={`switch-toggle-btn ${devMode === 'NONE' ? 'active' : ''}`} onClick={() => setDevMode('NONE')}>Sin Dev.</button>
              <button className={`switch-toggle-btn ${devMode === 'DEV' ? 'active' : ''}`} onClick={() => setDevMode('DEV')}>Con Dev.</button>
              <button className={`switch-toggle-btn ${devMode === 'FIN' ? 'active' : ''}`} onClick={() => setDevMode('FIN')}>Con D.FIN</button>
              <button className={`switch-toggle-btn ${devMode === 'NETO' ? 'active' : ''}`} onClick={() => setDevMode('NETO')}>NETO</button>
            </div>
            <div className="date-filters-group">
              <MultiSelectFilter
                label="Día" name="dias" icon={Calendar}
                options={Array.from({ length: 31 }, (_, i) => (i + 1).toString())}
                selectedValues={selectedDay}
                onChange={(name, vals) => setSelectedDay(vals)}
              />
              <MultiSelectFilter
                label="Mes" name="meses" icon={Calendar}
                options={nombresMeses}
                selectedValues={selectedMeses}
                onChange={(name, vals) => setSelectedMeses(vals)}
              />
              <MultiSelectFilter
                label="Año" name="anios" icon={Calendar}
                options={availableYears.map(year => ({ value: year, label: year.toString() }))}
                selectedValues={selectedAnios}
                onChange={(name, vals) => setSelectedAnios(vals)}
              />
            </div>
            <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
              <GlobalSearch
                availableOptions={availableOptions}
                filterLabels={filterLabels}
                filters={filters}
                setFilters={setFilters}
              />
            </div>
          </div>
        </header>

        {(() => {
          const bultosCumplimiento = Number(proveedorMarcaLineaTotals.cuota_bultos) > 0 ? (Number(proveedorMarcaLineaTotals.real_bultos) / Number(proveedorMarcaLineaTotals.cuota_bultos)) * 100 : 0;
          const timeVsSalesDiff = bultosCumplimiento - Number(workDayMetrics.percentage);
          return (
            <div className="working-days-bar">
              <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
              <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
              <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
              <div className="wd-separator"></div>
              <div className={`wd-item variation-item ${timeVsSalesDiff >= 0 ? 'positive' : 'negative'}`}>
                {timeVsSalesDiff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <strong>{timeVsSalesDiff.toFixed(1)}%</strong>
                <span className="wd-label">(vs Avance)</span>
              </div>
            </div>
          );
        })()}

        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros Disponibles</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters).map((key) => (
              <MultiSelectFilter
                key={key}
                label={filterLabels[key] || key}
                name={key}
                icon={key.includes('CLI') ? Users : Tag}
                options={availableOptions[key] || []}
                selectedValues={filters[key] || []}
                onChange={(name, vals) => {
                  setFilters(prev => ({ ...prev, [name]: vals }));
                }}
              />
            ))}
            <div className="filter-pill-container">
              <button className={`filter-pill-button ${lowCompliance ? 'active low-compliance' : ''}`} onClick={() => setLowCompliance(!lowCompliance)} title="Filtrar por cumplimiento menor al 80%">
                <span className="pill-label">Cumplimiento &lt; 80%</span>
              </button>
            </div>
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters} title="Restablecer filtros">
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        <div
          className="detail-table-container2"
          style={{
            opacity: querying ? 0.8 : 1,
            transition: 'opacity 0.3s',
            marginTop: '20px',
            height: isMobile ? 'auto' : undefined,
            overflow: isMobile ? 'visible' : undefined,
          }}
        >
          <div className="chart-container mini table-fixed-height" style={{ flex: 1, gridColumn: 'span 2' }}>
            <div className="table-header-container">
              <h3 className="chart-title">{breakdownLabels.title}</h3>
              <div className="chart-header-filters" style={{ flexWrap: 'wrap', gap: '8px' }}>
                <div className="switch-toggle-group">
                  <button className={`switch-toggle-btn ${breakdownType === 'REG_ASP' ? 'active' : ''}`} onClick={() => setBreakdownType('REG_ASP')}>Reg/ASP</button>
                  <button className={`switch-toggle-btn ${breakdownType === 'ESP_REG_VEN_CLI' ? 'active' : ''}`} onClick={() => setBreakdownType('ESP_REG_VEN_CLI')}>Esp.</button>{/* 🔧 NUEVO */}
                  <button className={`switch-toggle-btn ${breakdownType === 'ASP' ? 'active' : ''}`} onClick={() => setBreakdownType('ASP')}>ASP</button>
                  <button className={`switch-toggle-btn ${breakdownType === 'BI_PROVEEDOR' ? 'active' : ''}`} onClick={() => setBreakdownType('BI_PROVEEDOR')}>Casa</button>
                  <button className={`switch-toggle-btn ${breakdownType === 'REGION' ? 'active' : ''}`} onClick={() => setBreakdownType('REGION')}>Región</button>
                  <button className={`switch-toggle-btn ${breakdownType === 'CLIENTE' || breakdownType === 'CLI_PRIN' ? 'active' : ''}`} onClick={() => setBreakdownType('CLIENTE')}>Cliente</button>
                </div>
                <button className="export-table-btn" onClick={() => exportToExcel(proveedorMarcaLineaTableData, 'Sales_Export', 'proveedorMarcaLinea')}>
                  <Download size={12} />
                </button>
              </div>
            </div>

            {/* ========== VERSIÓN MÓVIL ========== */}
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
                {/* ── BULTOS MÓVIL ── */}
                <div>
                  <h4 style={{ margin: '0 0 6px 0', color: '#cbd5e1', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Package size={14} color="#cbd5e1" /> Bultos
                  </h4>
                  <div style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'hidden', borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#0f172a' }}>
                    <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.65rem', color: '#e2e8f0' }}>
                      <colgroup>
                        <col style={{ width: '40%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '10%' }} />
                      </colgroup>
                      <thead>
                        <tr style={{ position: 'sticky', top: 0, backgroundColor: '#1e293b', color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600, zIndex: 2 }}>
                          <th style={{ padding: '6px 4px', textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word' }}>{breakdownLabels.col1}</th>
                          <th style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</th>
                          <th style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>Real</th>
                          <th style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>% Efec.</th>
                          <th style={{ padding: '6px 4px', textAlign: 'center', whiteSpace: 'nowrap' }}>Est.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proveedorMarcaLineaTableData.map((prov, pIdx) => {
                          const efecBlProv = prov.cuota_bultos > 0 ? (prov.real_bultos / prov.cuota_bultos) * 100 : 0;
                          const isProvExpanded = expandedProveedores[prov.name];
                          return (
                            <React.Fragment key={`prov-bl-${pIdx}`}>
                              <tr onClick={() => toggleProveedor(prov.name)} style={{ cursor: 'pointer', backgroundColor: pIdx % 2 === 0 ? '#0f172a' : '#1e293b', borderBottom: '1px solid #334155' }}>
                                <td style={{ padding: '4px 4px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {isProvExpanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                  <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={prov.name}>{prov.name || '---'}</span>
                                </td>
                                <td style={{ padding: '4px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(prov.cuota_bultos)}</td>
                                <td style={{ padding: '4px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(prov.real_bultos)}</td>
                                <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecBlProv.toFixed(1)}%</td>
                                <td style={{ padding: '4px 4px', textAlign: 'center' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecBlProv), display: 'inline-block' }}></div></td>
                              </tr>
                              {isProvExpanded && prov.marcas.map((mar, mIdx) => {
                                const efecBlMar = mar.cuota_bultos > 0 ? (mar.real_bultos / mar.cuota_bultos) * 100 : 0;
                                const isMarExpanded = expandedMarcas[mar.name];
                                return (
                                  <React.Fragment key={`mar-bl-${pIdx}-${mIdx}`}>
                                    <tr onClick={() => toggleMarca(mar.name)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                      <td style={{ paddingLeft: '20px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {isMarExpanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={mar.name}>{mar.name || '---'}</span>
                                      </td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(mar.cuota_bultos)}</td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(mar.real_bultos)}</td>
                                      <td style={{ textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecBlMar.toFixed(1)}%</td>
                                      <td style={{ textAlign: 'center' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecBlMar), display: 'inline-block' }}></div></td>
                                    </tr>
                                    {isMarExpanded && (
                                      mar.clientes ? (
                                        mar.clientes.map((cli, cIdx) => {
                                          const efecBlCli = cli.cuota_bultos > 0 ? (cli.real_bultos / cli.cuota_bultos) * 100 : 0;
                                          const isCliExpanded = expandedClientes[cli.name];
                                          return (
                                            <React.Fragment key={`cli-bl-${pIdx}-${mIdx}-${cIdx}`}>
                                              <tr onClick={() => toggleCliente(cli.name)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                                <td style={{ paddingLeft: '35px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                  {isCliExpanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                                  <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={cli.name}>{cli.name || '---'}</span>
                                                </td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(cli.cuota_bultos)}</td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(cli.real_bultos)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecBlCli.toFixed(1)}%</td>
                                                <td style={{ textAlign: 'center' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecBlCli), display: 'inline-block' }}></div></td>
                                              </tr>
                                              {isCliExpanded && (
                                                cli.clientes4 ? (
                                                  /* 🔧 NIVEL 4 BULTOS MÓVIL */
                                                  cli.clientes4.map((cli4, c4Idx) => {
                                                    const efecBlCli4 = cli4.cuota_bultos > 0 ? (cli4.real_bultos / cli4.cuota_bultos) * 100 : 0;
                                                    const isCli4Expanded = expandedNivel4[cli4.name];
                                                    return (
                                                      <React.Fragment key={`cli4-bl-${pIdx}-${mIdx}-${cIdx}-${c4Idx}`}>
                                                        <tr onClick={() => toggleNivel4(cli4.name)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                                          <td style={{ paddingLeft: '50px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {isCli4Expanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                                            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={cli4.name}>{cli4.name || '---'}</span>
                                                          </td>
                                                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(cli4.cuota_bultos)}</td>
                                                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(cli4.real_bultos)}</td>
                                                          <td style={{ textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecBlCli4.toFixed(1)}%</td>
                                                          <td style={{ textAlign: 'center' }}><div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecBlCli4), display: 'inline-block' }}></div></td>
                                                        </tr>
                                                        {isCli4Expanded && cli4.lineas.map((lin, lIdx) => {
                                                          const efecBlLin = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                                          return (
                                                            <tr key={`lin-bl-${pIdx}-${mIdx}-${cIdx}-${c4Idx}-${lIdx}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155' }}>
                                                              <td style={{ paddingLeft: '65px', whiteSpace: 'normal', wordBreak: 'break-word' }} title={lin.name}>{lin.name || '---'}</td>
                                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(lin.cuota_bultos)}</td>
                                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(lin.real_bultos)}</td>
                                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{efecBlLin.toFixed(1)}%</td>
                                                              <td style={{ textAlign: 'center' }}><div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecBlLin), display: 'inline-block' }}></div></td>
                                                            </tr>
                                                          );
                                                        })}
                                                      </React.Fragment>
                                                    );
                                                  })
                                                ) : (
                                                  /* NIVEL 3 terminal (sin nivel 4) */
                                                  cli.lineas.map((lin, lIdx) => {
                                                    const efecBlLin = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                                    return (
                                                      <tr key={`lin-bl-${pIdx}-${mIdx}-${cIdx}-${lIdx}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155' }}>
                                                        <td style={{ paddingLeft: '50px', whiteSpace: 'normal', wordBreak: 'break-word' }} title={lin.name}>{lin.name || '---'}</td>
                                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(lin.cuota_bultos)}</td>
                                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(lin.real_bultos)}</td>
                                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{efecBlLin.toFixed(1)}%</td>
                                                        <td style={{ textAlign: 'center' }}><div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecBlLin), display: 'inline-block' }}></div></td>
                                                      </tr>
                                                    );
                                                  })
                                                )
                                              )}
                                            </React.Fragment>
                                          );
                                        })
                                      ) : (
                                        mar.lineas.map((lin, lIdx) => {
                                          const efecBlLin = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                          return (
                                            <tr key={`lin-bl-${pIdx}-${mIdx}-${lIdx}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155' }}>
                                              <td style={{ paddingLeft: '35px', whiteSpace: 'normal', wordBreak: 'break-word' }} title={lin.name}>{lin.name || '---'}</td>
                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(lin.cuota_bultos)}</td>
                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(lin.real_bultos)}</td>
                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{efecBlLin.toFixed(1)}%</td>
                                              <td style={{ textAlign: 'center' }}><div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecBlLin), display: 'inline-block' }}></div></td>
                                            </tr>
                                          );
                                        })
                                      )
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {(() => {
                          const totals = proveedorMarcaLineaTableData.reduce((acc, prov) => {
                            acc.real += Number(prov.real_bultos || 0);
                            acc.cuota += Number(prov.cuota_bultos || 0);
                            return acc;
                          }, { real: 0, cuota: 0 });
                          const efec = totals.cuota > 0 ? (totals.real / totals.cuota) * 100 : 0;
                          return (
                            <tr style={{ position: 'sticky', bottom: 0, backgroundColor: '#1e293b', fontWeight: 'bold', borderTop: '2px solid #475569', zIndex: 2 }}>
                              <td style={{ padding: '6px 4px', whiteSpace: 'normal', wordBreak: 'break-word' }}>TOTALES</td>
                              <td style={{ textAlign: 'right', padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatNumber(totals.cuota)}</td>
                              <td style={{ textAlign: 'right', padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatNumber(totals.real)}</td>
                              <td style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efec.toFixed(1)}%</td>
                              <td style={{ textAlign: 'center', padding: '6px 4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSemaforoColor(efec), display: 'inline-block' }}></div></td>
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* ── USD MÓVIL ── */}
                <div style={{ paddingBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 6px 0', color: '#cbd5e1', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <DollarSign size={14} color="#cbd5e1" /> USD
                  </h4>
                  <div style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'hidden', borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#0f172a' }}>
                    <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '0.65rem', color: '#e2e8f0' }}>
                      <colgroup>
                        <col style={{ width: '40%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '10%' }} />
                      </colgroup>
                      <thead>
                        <tr style={{ position: 'sticky', top: 0, backgroundColor: '#1e293b', color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.6rem', fontWeight: 600, zIndex: 2 }}>
                          <th style={{ padding: '6px 4px', textAlign: 'left', whiteSpace: 'normal', wordBreak: 'break-word' }}>{breakdownLabels.col1}</th>
                          <th style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</th>
                          <th style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>Real</th>
                          <th style={{ padding: '6px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>% Efec.</th>
                          <th style={{ padding: '6px 4px', textAlign: 'center', whiteSpace: 'nowrap' }}>Est.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {proveedorMarcaLineaTableData.map((prov, pIdx) => {
                          const efecUsdProv = prov.cuota_usd > 0 ? (prov.real_usd / prov.cuota_usd) * 100 : 0;
                          const isProvExpanded = expandedProveedores[prov.name];
                          return (
                            <React.Fragment key={`prov-usd-${pIdx}`}>
                              <tr onClick={() => toggleProveedor(prov.name)} style={{ cursor: 'pointer', backgroundColor: pIdx % 2 === 0 ? '#0f172a' : '#1e293b', borderBottom: '1px solid #334155' }}>
                                <td style={{ padding: '4px 4px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                  {isProvExpanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                  <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={prov.name}>{prov.name || '---'}</span>
                                </td>
                                <td style={{ padding: '4px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(prov.cuota_usd)}</td>
                                <td style={{ padding: '4px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(prov.real_usd)}</td>
                                <td style={{ padding: '4px 4px', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecUsdProv.toFixed(1)}%</td>
                                <td style={{ padding: '4px 4px', textAlign: 'center' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecUsdProv), display: 'inline-block' }}></div></td>
                              </tr>
                              {isProvExpanded && prov.marcas.map((mar, mIdx) => {
                                const efecUsdMar = mar.cuota_usd > 0 ? (mar.real_usd / mar.cuota_usd) * 100 : 0;
                                const isMarExpanded = expandedMarcas[mar.name];
                                return (
                                  <React.Fragment key={`mar-usd-${pIdx}-${mIdx}`}>
                                    <tr onClick={() => toggleMarca(mar.name)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                      <td style={{ paddingLeft: '20px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {isMarExpanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={mar.name}>{mar.name || '---'}</span>
                                      </td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(mar.cuota_usd)}</td>
                                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(mar.real_usd)}</td>
                                      <td style={{ textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecUsdMar.toFixed(1)}%</td>
                                      <td style={{ textAlign: 'center' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecUsdMar), display: 'inline-block' }}></div></td>
                                    </tr>
                                    {isMarExpanded && (
                                      mar.clientes ? (
                                        mar.clientes.map((cli, cIdx) => {
                                          const efecUsdCli = cli.cuota_usd > 0 ? (cli.real_usd / cli.cuota_usd) * 100 : 0;
                                          const isCliExpanded = expandedClientes[cli.name];
                                          return (
                                            <React.Fragment key={`cli-usd-${pIdx}-${mIdx}-${cIdx}`}>
                                              <tr onClick={() => toggleCliente(cli.name)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                                <td style={{ paddingLeft: '35px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                  {isCliExpanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                                  <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={cli.name}>{cli.name || '---'}</span>
                                                </td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(cli.cuota_usd)}</td>
                                                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(cli.real_usd)}</td>
                                                <td style={{ textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecUsdCli.toFixed(1)}%</td>
                                                <td style={{ textAlign: 'center' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecUsdCli), display: 'inline-block' }}></div></td>
                                              </tr>
                                              {isCliExpanded && (
                                                cli.clientes4 ? (
                                                  /* 🔧 NIVEL 4 USD MÓVIL */
                                                  cli.clientes4.map((cli4, c4Idx) => {
                                                    const efecUsdCli4 = cli4.cuota_usd > 0 ? (cli4.real_usd / cli4.cuota_usd) * 100 : 0;
                                                    const isCli4Expanded = expandedNivel4[cli4.name];
                                                    return (
                                                      <React.Fragment key={`cli4-usd-${pIdx}-${mIdx}-${cIdx}-${c4Idx}`}>
                                                        <tr onClick={() => toggleNivel4(cli4.name)} style={{ cursor: 'pointer', backgroundColor: '#1e293b', borderBottom: '1px solid #334155' }}>
                                                          <td style={{ paddingLeft: '50px', display: 'flex', alignItems: 'flex-start', gap: '3px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {isCli4Expanded ? <ChevronDown size={12} style={{ flexShrink: 0, marginTop: '2px' }} /> : <ChevronRight size={12} style={{ flexShrink: 0, marginTop: '2px' }} />}
                                                            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }} title={cli4.name}>{cli4.name || '---'}</span>
                                                          </td>
                                                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(cli4.cuota_usd)}</td>
                                                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(cli4.real_usd)}</td>
                                                          <td style={{ textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efecUsdCli4.toFixed(1)}%</td>
                                                          <td style={{ textAlign: 'center' }}><div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecUsdCli4), display: 'inline-block' }}></div></td>
                                                        </tr>
                                                        {isCli4Expanded && cli4.lineas.map((lin, lIdx) => {
                                                          const efecUsdLin = lin.cuota_usd > 0 ? (lin.real_usd / lin.cuota_usd) * 100 : 0;
                                                          return (
                                                            <tr key={`lin-usd-${pIdx}-${mIdx}-${cIdx}-${c4Idx}-${lIdx}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155' }}>
                                                              <td style={{ paddingLeft: '65px', whiteSpace: 'normal', wordBreak: 'break-word' }} title={lin.name}>{lin.name || '---'}</td>
                                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(lin.cuota_usd)}</td>
                                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(lin.real_usd)}</td>
                                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{efecUsdLin.toFixed(1)}%</td>
                                                              <td style={{ textAlign: 'center' }}><div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecUsdLin), display: 'inline-block' }}></div></td>
                                                            </tr>
                                                          );
                                                        })}
                                                      </React.Fragment>
                                                    );
                                                  })
                                                ) : (
                                                  cli.lineas.map((lin, lIdx) => {
                                                    const efecUsdLin = lin.cuota_usd > 0 ? (lin.real_usd / lin.cuota_usd) * 100 : 0;
                                                    return (
                                                      <tr key={`lin-usd-${pIdx}-${mIdx}-${cIdx}-${lIdx}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155' }}>
                                                        <td style={{ paddingLeft: '50px', whiteSpace: 'normal', wordBreak: 'break-word' }} title={lin.name}>{lin.name || '---'}</td>
                                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(lin.cuota_usd)}</td>
                                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(lin.real_usd)}</td>
                                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{efecUsdLin.toFixed(1)}%</td>
                                                        <td style={{ textAlign: 'center' }}><div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecUsdLin), display: 'inline-block' }}></div></td>
                                                      </tr>
                                                    );
                                                  })
                                                )
                                              )}
                                            </React.Fragment>
                                          );
                                        })
                                      ) : (
                                        mar.lineas.map((lin, lIdx) => {
                                          const efecUsdLin = lin.cuota_usd > 0 ? (lin.real_usd / lin.cuota_usd) * 100 : 0;
                                          return (
                                            <tr key={`lin-usd-${pIdx}-${mIdx}-${lIdx}`} style={{ backgroundColor: '#0f172a', borderBottom: '1px solid #334155' }}>
                                              <td style={{ paddingLeft: '35px', whiteSpace: 'normal', wordBreak: 'break-word' }} title={lin.name}>{lin.name || '---'}</td>
                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(lin.cuota_usd)}</td>
                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDollar(lin.real_usd)}</td>
                                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{efecUsdLin.toFixed(1)}%</td>
                                              <td style={{ textAlign: 'center' }}><div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: getSemaforoColor(efecUsdLin), display: 'inline-block' }}></div></td>
                                            </tr>
                                          );
                                        })
                                      )
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {(() => {
                          const totals = proveedorMarcaLineaTableData.reduce((acc, prov) => {
                            acc.real += Number(prov.real_usd || 0);
                            acc.cuota += Number(prov.cuota_usd || 0);
                            return acc;
                          }, { real: 0, cuota: 0 });
                          const efec = totals.cuota > 0 ? (totals.real / totals.cuota) * 100 : 0;
                          return (
                            <tr style={{ position: 'sticky', bottom: 0, backgroundColor: '#1e293b', fontWeight: 'bold', borderTop: '2px solid #475569', zIndex: 2 }}>
                              <td style={{ padding: '6px 4px', whiteSpace: 'normal', wordBreak: 'break-word' }}>TOTALES</td>
                              <td style={{ textAlign: 'right', padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatDollar(totals.cuota)}</td>
                              <td style={{ textAlign: 'right', padding: '6px 4px', whiteSpace: 'nowrap' }}>{formatDollar(totals.real)}</td>
                              <td style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{efec.toFixed(1)}%</td>
                              <td style={{ textAlign: 'center', padding: '6px 4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getSemaforoColor(efec), display: 'inline-block' }}></div></td>
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              /* ========== VERSIÓN ESCRITORIO ========== */
              <div className="minimal-table-wrapper">
                <div className="table-header5">
                  <div className="col-name">{breakdownLabels.col1}</div>
                  <div className="col-data">{comparisonMode === 'FORECAST' ? 'FC BL' : 'Cuota BL'}</div>
                  <div className="col-data">Real BL</div>
                  <div className="col-data">% Efec. BL</div>
                  <div className="col-status">Est. BL</div>
                  <div className="col-var">Var. Anual BL</div>
                  <div className="col-data">{comparisonMode === 'FORECAST' ? 'FC USD' : 'Cuota USD'}</div>
                  <div className="col-data">Real USD</div>
                  <div className="col-data">% Efec. USD</div>
                  <div className="col-status">Est. USD</div>
                  <div className="col-var">Var. Anual USD</div>
                </div>

                <div className="table-body5">
                  {proveedorMarcaLineaTableData && proveedorMarcaLineaTableData.map((prov, pIdx) => {
                    const efecBlProv = prov.cuota_bultos > 0 ? (prov.real_bultos / prov.cuota_bultos) * 100 : 0;
                    const efecUsdProv = prov.cuota_usd > 0 ? (prov.real_usd / prov.cuota_usd) * 100 : 0;
                    const isProvExpanded = expandedProveedores[prov.name];
                    const varBlProv = prov.var_bultos;
                    const varUsdProv = prov.var_usd;
                    return (
                      <React.Fragment key={`prov-${pIdx}`}>
                        <div className="table-row5 advisor-row" onClick={() => toggleProveedor(prov.name)}>
                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {isProvExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span title={prov.name}>{prov.name || '---'}</span>
                          </div>
                          <div className="col-data">{formatNumber(prov.cuota_bultos)}</div>
                          <div className="col-data">{formatNumber(prov.real_bultos)}</div>
                          <div className="col-data bold">{efecBlProv.toFixed(1)}%</div>
                          <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecBlProv) }}></div></div>
                          <div className="col-var" style={{ fontWeight: 'bold', color: (varBlProv || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varBlProv != null ? `${varBlProv.toFixed(1)}%` : '--'}</div>
                          <div className="col-data">{formatDollar(prov.cuota_usd)}</div>
                          <div className="col-data">{formatDollar(prov.real_usd)}</div>
                          <div className="col-data bold">{efecUsdProv.toFixed(1)}%</div>
                          <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecUsdProv) }}></div></div>
                          <div className="col-var" style={{ fontWeight: 'bold', color: (varUsdProv || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varUsdProv != null ? `${varUsdProv.toFixed(1)}%` : '--'}</div>
                        </div>
                        {isProvExpanded && prov.marcas.map((mar, mIdx) => {
                          const efecBlMar = mar.cuota_bultos > 0 ? (mar.real_bultos / mar.cuota_bultos) * 100 : 0;
                          const efecUsdMar = mar.cuota_usd > 0 ? (mar.real_usd / mar.cuota_usd) * 100 : 0;
                          const isMarExpanded = expandedMarcas[mar.name];
                          const varBlMar = mar.var_bultos;
                          const varUsdMar = mar.var_usd;
                          return (
                            <React.Fragment key={`mar-${pIdx}-${mIdx}`}>
                              <div className="table-row5" style={{ backgroundColor: 'rgba(0,0,0,0.1)' }} onClick={() => toggleMarca(mar.name)}>
                                <div className="col-name truncate" style={{ paddingLeft: '20px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                  {isMarExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  <span title={mar.name}>{mar.name || '---'}</span>
                                </div>
                                <div className="col-data">{formatNumber(mar.cuota_bultos)}</div>
                                <div className="col-data">{formatNumber(mar.real_bultos)}</div>
                                <div className="col-data bold">{efecBlMar.toFixed(1)}%</div>
                                <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecBlMar) }}></div></div>
                                <div className="col-var" style={{ fontWeight: 'bold', color: (varBlMar || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varBlMar != null ? `${varBlMar.toFixed(1)}%` : '--'}</div>
                                <div className="col-data">{formatDollar(mar.cuota_usd)}</div>
                                <div className="col-data">{formatDollar(mar.real_usd)}</div>
                                <div className="col-data bold">{efecUsdMar.toFixed(1)}%</div>
                                <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecUsdMar) }}></div></div>
                                <div className="col-var" style={{ fontWeight: 'bold', color: (varUsdMar || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varUsdMar != null ? `${varUsdMar.toFixed(1)}%` : '--'}</div>
                              </div>
                              {isMarExpanded && (
                                mar.clientes ? (
                                  mar.clientes.map((cli, cIdx) => {
                                    const efecBlCli = cli.cuota_bultos > 0 ? (cli.real_bultos / cli.cuota_bultos) * 100 : 0;
                                    const efecUsdCli = cli.cuota_usd > 0 ? (cli.real_usd / cli.cuota_usd) * 100 : 0;
                                    const isCliExpanded = expandedClientes[cli.name];
                                    const varBlCli = cli.var_bultos;
                                    const varUsdCli = cli.var_usd;
                                    return (
                                      <React.Fragment key={`cli-${pIdx}-${mIdx}-${cIdx}`}>
                                        <div className="table-row5" style={{ backgroundColor: 'rgba(0,0,0,0.15)', paddingLeft: '40px' }} onClick={() => toggleCliente(cli.name)}>
                                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            {isCliExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            <span title={cli.name}>{cli.name || '---'}</span>
                                          </div>
                                          <div className="col-data">{formatNumber(cli.cuota_bultos)}</div>
                                          <div className="col-data">{formatNumber(cli.real_bultos)}</div>
                                          <div className="col-data bold">{efecBlCli.toFixed(1)}%</div>
                                          <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecBlCli) }}></div></div>
                                          <div className="col-var" style={{ fontWeight: 'bold', color: (varBlCli || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varBlCli != null ? `${varBlCli.toFixed(1)}%` : '--'}</div>
                                          <div className="col-data">{formatDollar(cli.cuota_usd)}</div>
                                          <div className="col-data">{formatDollar(cli.real_usd)}</div>
                                          <div className="col-data bold">{efecUsdCli.toFixed(1)}%</div>
                                          <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecUsdCli) }}></div></div>
                                          <div className="col-var" style={{ fontWeight: 'bold', color: (varUsdCli || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varUsdCli != null ? `${varUsdCli.toFixed(1)}%` : '--'}</div>
                                        </div>
                                        {isCliExpanded && (
                                          cli.clientes4 ? (
                                            /* 🔧 NIVEL 4 ESCRITORIO */
                                            cli.clientes4.map((cli4, c4Idx) => {
                                              const efecBlCli4  = cli4.cuota_bultos > 0 ? (cli4.real_bultos / cli4.cuota_bultos) * 100 : 0;
                                              const efecUsdCli4 = cli4.cuota_usd    > 0 ? (cli4.real_usd    / cli4.cuota_usd)    * 100 : 0;
                                              const isCli4Expanded = expandedNivel4[cli4.name];
                                              const varBlCli4  = cli4.var_bultos;
                                              const varUsdCli4 = cli4.var_usd;
                                              return (
                                                <React.Fragment key={`cli4-${pIdx}-${mIdx}-${cIdx}-${c4Idx}`}>
                                                  <div className="table-row5" style={{ backgroundColor: 'rgba(0,0,0,0.18)', paddingLeft: '60px' }} onClick={() => toggleNivel4(cli4.name)}>
                                                    <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                      {isCli4Expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                      <span title={cli4.name}>{cli4.name || '---'}</span>
                                                    </div>
                                                    <div className="col-data">{formatNumber(cli4.cuota_bultos)}</div>
                                                    <div className="col-data">{formatNumber(cli4.real_bultos)}</div>
                                                    <div className="col-data bold">{efecBlCli4.toFixed(1)}%</div>
                                                    <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecBlCli4) }}></div></div>
                                                    <div className="col-var" style={{ fontWeight: 'bold', color: (varBlCli4 || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varBlCli4 != null ? `${varBlCli4.toFixed(1)}%` : '--'}</div>
                                                    <div className="col-data">{formatDollar(cli4.cuota_usd)}</div>
                                                    <div className="col-data">{formatDollar(cli4.real_usd)}</div>
                                                    <div className="col-data bold">{efecUsdCli4.toFixed(1)}%</div>
                                                    <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecUsdCli4) }}></div></div>
                                                    <div className="col-var" style={{ fontWeight: 'bold', color: (varUsdCli4 || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varUsdCli4 != null ? `${varUsdCli4.toFixed(1)}%` : '--'}</div>
                                                  </div>
                                                  {isCli4Expanded && cli4.lineas.map((lin, lIdx) => {
                                                    const efecBlLin  = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                                    const efecUsdLin = lin.cuota_usd    > 0 ? (lin.real_usd    / lin.cuota_usd)    * 100 : 0;
                                                    const varBlLin   = lin.var_bultos;
                                                    const varUsdLin  = lin.var_usd;
                                                    return (
                                                      <div className="table-row5" key={`lin-${pIdx}-${mIdx}-${cIdx}-${c4Idx}-${lIdx}`} style={{ backgroundColor: 'rgba(0,0,0,0.25)', fontSize: '0.7rem' }}>
                                                        <div className="col-name truncate" style={{ paddingLeft: '80px' }} title={lin.name}>{lin.name || '---'}</div>
                                                        <div className="col-data">{formatNumber(lin.cuota_bultos)}</div>
                                                        <div className="col-data">{formatNumber(lin.real_bultos)}</div>
                                                        <div className="col-data">{efecBlLin.toFixed(1)}%</div>
                                                        <div className="col-status"><div className="status-dot" style={{ width: '5px', height: '5px', backgroundColor: getSemaforoColor(efecBlLin) }}></div></div>
                                                        <div className="col-var" style={{ fontWeight: 'bold', color: (varBlLin || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varBlLin != null ? `${varBlLin.toFixed(1)}%` : '--'}</div>
                                                        <div className="col-data">{formatDollar(lin.cuota_usd)}</div>
                                                        <div className="col-data">{formatDollar(lin.real_usd)}</div>
                                                        <div className="col-data">{efecUsdLin.toFixed(1)}%</div>
                                                        <div className="col-status"><div className="status-dot" style={{ width: '5px', height: '5px', backgroundColor: getSemaforoColor(efecUsdLin) }}></div></div>
                                                        <div className="col-var" style={{ fontWeight: 'bold', color: (varUsdLin || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varUsdLin != null ? `${varUsdLin.toFixed(1)}%` : '--'}</div>
                                                      </div>
                                                    );
                                                  })}
                                                </React.Fragment>
                                              );
                                            })
                                          ) : (
                                            cli.lineas.map((lin, lIdx) => {
                                              const efecBlLin = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                              const efecUsdLin = lin.cuota_usd > 0 ? (lin.real_usd / lin.cuota_usd) * 100 : 0;
                                              const varBlLin = lin.var_bultos;
                                              const varUsdLin = lin.var_usd;
                                              return (
                                                <div className="table-row5" key={`lin-${pIdx}-${mIdx}-${cIdx}-${lIdx}`} style={{ backgroundColor: 'rgba(0,0,0,0.2)', fontSize: '0.7rem' }}>
                                                  <div className="col-name truncate" style={{ paddingLeft: '60px' }} title={lin.name}>{lin.name || '---'}</div>
                                                  <div className="col-data">{formatNumber(lin.cuota_bultos)}</div>
                                                  <div className="col-data">{formatNumber(lin.real_bultos)}</div>
                                                  <div className="col-data">{efecBlLin.toFixed(1)}%</div>
                                                  <div className="col-status"><div className="status-dot" style={{ width: '6px', height: '6px', backgroundColor: getSemaforoColor(efecBlLin) }}></div></div>
                                                  <div className="col-var" style={{ fontWeight: 'bold', color: (varBlLin || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varBlLin != null ? `${varBlLin.toFixed(1)}%` : '--'}</div>
                                                  <div className="col-data">{formatDollar(lin.cuota_usd)}</div>
                                                  <div className="col-data">{formatDollar(lin.real_usd)}</div>
                                                  <div className="col-data">{efecUsdLin.toFixed(1)}%</div>
                                                  <div className="col-status"><div className="status-dot" style={{ width: '6px', height: '6px', backgroundColor: getSemaforoColor(efecUsdLin) }}></div></div>
                                                  <div className="col-var" style={{ fontWeight: 'bold', color: (varUsdLin || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varUsdLin != null ? `${varUsdLin.toFixed(1)}%` : '--'}</div>
                                                </div>
                                              );
                                            })
                                          )
                                        )}
                                      </React.Fragment>
                                    );
                                  })
                                ) : (
                                  mar.lineas.map((lin, lIdx) => {
                                    const efecBlLin = lin.cuota_bultos > 0 ? (lin.real_bultos / lin.cuota_bultos) * 100 : 0;
                                    const efecUsdLin = lin.cuota_usd > 0 ? (lin.real_usd / lin.cuota_usd) * 100 : 0;
                                    const varBlLin = lin.var_bultos;
                                    const varUsdLin = lin.var_usd;
                                    return (
                                      <div className="table-row5" key={`lin-${pIdx}-${mIdx}-${lIdx}`} style={{ backgroundColor: 'rgba(0,0,0,0.2)', fontSize: '0.7rem' }}>
                                        <div className="col-name truncate" style={{ paddingLeft: '40px' }} title={lin.name}>{lin.name || '---'}</div>
                                        <div className="col-data">{formatNumber(lin.cuota_bultos)}</div>
                                        <div className="col-data">{formatNumber(lin.real_bultos)}</div>
                                        <div className="col-data">{efecBlLin.toFixed(1)}%</div>
                                        <div className="col-status"><div className="status-dot" style={{ width: '6px', height: '6px', backgroundColor: getSemaforoColor(efecBlLin) }}></div></div>
                                        <div className="col-var" style={{ fontWeight: 'bold', color: (varBlLin || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varBlLin != null ? `${varBlLin.toFixed(1)}%` : '--'}</div>
                                        <div className="col-data">{formatDollar(lin.cuota_usd)}</div>
                                        <div className="col-data">{formatDollar(lin.real_usd)}</div>
                                        <div className="col-data">{efecUsdLin.toFixed(1)}%</div>
                                        <div className="col-status"><div className="status-dot" style={{ width: '6px', height: '6px', backgroundColor: getSemaforoColor(efecUsdLin) }}></div></div>
                                        <div className="col-var" style={{ fontWeight: 'bold', color: (varUsdLin || 0) >= 0 ? '#00ba52' : '#ff4d4d' }}>{varUsdLin != null ? `${varUsdLin.toFixed(1)}%` : '--'}</div>
                                      </div>
                                    );
                                  })
                                )
                              )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className="table-footer5">
                  <div className="col-name">TOTALES</div>
                  {(() => {
                    const totals = proveedorMarcaLineaTableData.reduce((acc, prov) => {
                      acc.real_bultos += Number(prov.real_bultos || 0);
                      acc.cuota_bultos += Number(prov.cuota_bultos || 0);
                      acc.real_usd += Number(prov.real_usd || 0);
                      acc.cuota_usd += Number(prov.cuota_usd || 0);
                      acc.real_bultos_prev += Number(prov.real_bultos_prev || 0);
                      acc.real_usd_prev += Number(prov.real_usd_prev || 0);
                      return acc;
                    }, { real_bultos: 0, cuota_bultos: 0, real_usd: 0, cuota_usd: 0, real_bultos_prev: 0, real_usd_prev: 0 });

                    const calcVar = (actual, prev) => {
                      if (prev == null || isNaN(prev) || prev === 0) return actual > 0 ? 100 : 0;
                      return ((actual - prev) / prev) * 100;
                    };
                    const totalVarBL = calcVar(totals.real_bultos, totals.real_bultos_prev);
                    const totalVarUSD = calcVar(totals.real_usd, totals.real_usd_prev);
                    const efectividad_bl = totals.cuota_bultos > 0 ? (totals.real_bultos / totals.cuota_bultos) * 100 : 0;
                    const efectividad_usd = totals.cuota_usd > 0 ? (totals.real_usd / totals.cuota_usd) * 100 : 0;

                    return (
                      <>
                        <div className="col-data">{formatNumber(totals.cuota_bultos)}</div>
                        <div className="col-data">{formatNumber(totals.real_bultos)}</div>
                        <div className="col-data bold">{efectividad_bl.toFixed(1)}%</div>
                        <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efectividad_bl) }}></div></div>
                        <div className="col-var" style={{ fontWeight: 'bold', color: totalVarBL >= 0 ? '#00ba52' : '#ff4d4d' }}>{totalVarBL != null ? `${totalVarBL.toFixed(1)}%` : '--'}</div>
                        <div className="col-data">{formatNumber(totals.cuota_usd, true)}</div>
                        <div className="col-data">{formatNumber(totals.real_usd, true)}</div>
                        <div className="col-data bold">{efectividad_usd.toFixed(1)}%</div>
                        <div className="col-status"><div className="status-dot" style={{ backgroundColor: getSemaforoColor(efectividad_usd) }}></div></div>
                        <div className="col-var" style={{ fontWeight: 'bold', color: totalVarUSD >= 0 ? '#00ba52' : '#ff4d4d' }}>{totalVarUSD != null ? `${totalVarUSD.toFixed(1)}%` : '--'}</div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>

        <footer className="status-footer">
          {querying ? "SINCRONIZANDO..." : `VISTA ACTUAL: ${selectedMeses.length > 0 ? selectedMeses.join(', ') : 'TODOS'} ${selectedAnios.length > 0 ? selectedAnios.join(', ') : ''}`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default DailyPrincipal;